// PromoEntity service
//
// A PromoEntity is a "thing" that can be/is being pitched.
//
// Previously the only possible "thing" was a Source, but now we also have
// introduced the ability to pitch a Product (and, some support was previously
// added for pitching a Company, but that was never completed).
//
// So, although we don't have an abstract PromoEntity model, you can think of it as:
//
// PromoEntity
//  |
//  +-- Source
//  |
//  +-- Product
//  |
//  +-- (Company)

import vapi from '../../../javascript/frontend/api/vapi';

const generateUniqueString = () => Math.random().toString(36).substr(2, 4);

// The API stringifies scalars, so has_paid_representative arrives as 'true'/'false' - and as undefined when the
// caller did not ask for it by name (see fetchable_fields on the source resource), which must stay undefined so
// the UI can tell "not paid" apart from "not requested".
const paidFromApi = (isPaid) => (isPaid === undefined ? undefined : isPaid === 'true');

// has_paid_representative is only served to callers that name it in a sparse fieldset, which means naming every
// source field read below. Any query whose sources need a plan badge asks for this list.
const SOURCE_PROMO_ENTITY_FIELDS = [
  'full_name',
  'avatar_cloudinary_url',
  'company_name',
  'employments_string_without_company',
  'is_self_expert',
  'self_expert_user_id',
  'first_name',
  'has_paid_representative',
].join(',');

const subheadingForSource = (companyName, jobTitle) => {
  if (companyName && jobTitle) {
    return `${jobTitle} at ${companyName}`;
  }

  return jobTitle || companyName;
};

const newPromoEntity = (type, data) => {
  const basicRepresentation = {
    localId: generateUniqueString(),
    type,
  };

  if (type === 'Source') {
    return {
      ...basicRepresentation,
      name: data.name,
      jobTitle: data.jobTitle,
      companyName: data.companyName,
      companyId: data.companyId,
      selfExpert: data.isSelfExpert,
      subheading: subheadingForSource(data.companyName, data.jobTitle),
    };
  }

  if (type === 'Product') {
    return {
      ...basicRepresentation,
      name: data.name,
      productUrl: data.productUrl,
      companyName: data.companyName,
      companyId: data.companyId,
      subheading: `by ${data.companyName}`,
    };
  }

  throw new Error(`Unknown promoted entity type: ${type}`);
};

// Converts an API representation of a promo entity to our frontend representation
const formattedPromoEntity = (apiPromoEntity, localId, currentUserId) => {
  const remoteToLocalTypes = { sources: 'Source', products: 'Product' };

  const basicRepresentation = {
    localId: localId || generateUniqueString(), // keep consistent localIds when persisting new promo entities
    id: apiPromoEntity.id,
    type: remoteToLocalTypes[apiPromoEntity.type],
  };

  if (basicRepresentation.type === 'Source') {
    return {
      ...basicRepresentation,
      name: apiPromoEntity.attributes.full_name,
      profileImageUrl: apiPromoEntity.attributes.avatar_cloudinary_url,
      companyName: apiPromoEntity.attributes.company_name,
      jobTitle: apiPromoEntity.attributes.employments_string_without_company,
      selfExpert: apiPromoEntity.attributes.is_self_expert === 'true',
      selfExpertUserId: apiPromoEntity.attributes.self_expert_user_id,
      firstName: apiPromoEntity.attributes.first_name,
      // Whether this expert IS the viewer, which the plan tooltip addresses in the first person. Guarded on
      // presence so two undefineds don't compare equal.
      isCurrentUser: Boolean(apiPromoEntity.attributes.self_expert_user_id)
        && String(apiPromoEntity.attributes.self_expert_user_id) === String(currentUserId),
      isPaid: paidFromApi(apiPromoEntity.attributes.has_paid_representative),
      subheading: subheadingForSource(
        apiPromoEntity.attributes.company_name,
        apiPromoEntity.attributes.employments_string_without_company,
      ),
    };
  }

  if (basicRepresentation.type === 'Product') {
    return {
      ...basicRepresentation,
      name: apiPromoEntity.attributes.name,
      profileImageUrl: apiPromoEntity.attributes.image_url,
      companyName: apiPromoEntity.attributes.company_name,
      subheading: `by ${apiPromoEntity.attributes.company_name}`,
    };
  }

  throw new Error(`Unknown promoted entity type: ${basicRepresentation.type}`);
};

const convertApiPromoEntityFromBulkFetchToFrontendFormat = (apiPromoEntity, currentUserId) => (
  formattedPromoEntity(apiPromoEntity, null, currentUserId)
);

const convertApiPromoEntityToFrontendFormat = (apiPromoEntity, localId = null, currentUserId = undefined) => (
  formattedPromoEntity(apiPromoEntity, localId, currentUserId)
);

const fetchPromoEntitiesRepresentedBy = async (userId) => {
  if (!userId) {
    throw new Error('fetchPromoEntitiesRepresentedBy: userId is required');
  }

  // Products are left unrestricted: only experts carry a plan badge.
  const query = `/api/internal/jsonapi/users/${userId}`
    + `?include=sources,products&fields[source]=${SOURCE_PROMO_ENTITY_FIELDS}`;
  const response = await fetch(query, { method: 'GET' });
  const json = await response.json();
  if (!json.included) { return []; }
  const apiUserPromoEntities = json.included;
  const promotedEntities = apiUserPromoEntities.map(
    (apiPromoEntity) => convertApiPromoEntityFromBulkFetchToFrontendFormat(apiPromoEntity, userId),
  );
  return promotedEntities;
};

// Since we are going to be calling vapi.createSource, vapi.createProduct, etc,
// which call our internal APIs (e.g. /api/internal/sources), we need to prepare
// the data in the format expected by those APIs.
const requestDataForPromoEntityCreation = (data) => {
  if (data.type == 'Source') {
    // Data expected by /api/internal/sources
    return {
      full_name: data.name,
      title: data.jobTitle,
      company_id: data.companyId,
      company_name: data.companyName,
      is_self_source: data.selfExpert,
    };
  }

  if (data.type == 'Product') {
    // Data expected by /api/internal/products
    return {
      name: data.name,
      product_url: data.productUrl,
      company_id: data.companyId,
      company_name: data.companyName,
    };
  }
};

const createPromoEntity = async (localId, data, currentUserId) => {
  const requestData = requestDataForPromoEntityCreation(data);
  const response = await vapi.createPromoEntity(data.type, requestData);
  const promoEntity = convertApiPromoEntityToFrontendFormat(response.data.data, localId, currentUserId);

  return promoEntity;
};

const persistNewPromoEntity = async (entity, currentUserId) => {
  if (entity.existing) { return entity; }

  // TODO: handle possible error?
  const persistedPromoEntity = await createPromoEntity(entity.new.localId, entity.new, currentUserId);
  return { existing: persistedPromoEntity };
};

const deletePromoEntity = async (entityId) => {
  await vapi.deletePromoEntity(entityId);
};

export {
  fetchPromoEntitiesRepresentedBy,
  newPromoEntity,
  createPromoEntity,
  deletePromoEntity,
  persistNewPromoEntity,
  paidFromApi,
  SOURCE_PROMO_ENTITY_FIELDS,
};
